import { useFrame } from '@react-three/fiber';
import { useRef } from 'react';
import { AmbientLight, Vector3 } from 'three';

import AnimatePresence from '0_canvas/AnimatePresence/AnimatePresence';
import AnimationController from '0_canvas/AnimationController/AnimationController';
import BushfirePeril, {
  type BushfirePerilRef,
} from '0_canvas/Perils/BushfirePeril/BushfirePeril';
import CyclonePeril, {
  type CyclonePerilRef,
} from '0_canvas/Perils/CyclonePeril/CyclonePeril';
import ThunderstormPeril from '0_canvas/Perils/ThunderstormPeril/ThunderstormPeril';
import WaterPeril from '0_canvas/Perils/WaterPeril/WaterPeril';
import { PERIL_STAGE, useThreeStore } from 'state/threeStore';

import {
  AMBIENT_LIGHT_MAX_INTENSITY,
  hideAmbientLight,
  hideThunderstorm,
  hideWater,
  revealAmbientLight,
  revealThunderstorm,
  revealWater,
} from './PerilScene.animations';

export const WATER_PERIL_INITIAL_POSITION = new Vector3(0, -100, 0);
export const THUNDERSTORM_PERIL_INITIAL_POSITION = new Vector3(0, 120, 0);

const getPerilsState = () => useThreeStore.getState().peril;
const areTilesRendered = () => !!useThreeStore.getState().tiles.isRendered;

const shouldPerilRender = (name: PERIL_STAGE) =>
  getPerilsState().isRendered &&
  getPerilsState().stage === name &&
  areTilesRendered();

const PerilsScene = () => {
  const ambientLightRef = useRef<AmbientLight>(null!);
  const shouldShowDefaultLightRef = useRef<boolean>(false);

  const cycloneRef = useRef<CyclonePerilRef>(null!);
  const bushfireRef = useRef<BushfirePerilRef>(null!);

  useFrame(() => {
    const shouldShowDefaultLight = !shouldPerilRender('storm');
    if (shouldShowDefaultLightRef.current === shouldShowDefaultLight) return;
    shouldShowDefaultLightRef.current = shouldShowDefaultLight;

    if (shouldShowDefaultLight) revealAmbientLight(ambientLightRef.current);
    else hideAmbientLight(ambientLightRef.current);
  });

  const perils = (
    <group>
      <AnimatePresence
        showAnimation={revealThunderstorm}
        hideAnimation={hideThunderstorm}
        getIsPresent={() => shouldPerilRender('storm')}
        position={THUNDERSTORM_PERIL_INITIAL_POSITION}
        visible={false}
      >
        <ThunderstormPeril />
      </AnimatePresence>

      <AnimatePresence
        showAnimation={revealWater}
        hideAnimation={hideWater}
        getIsPresent={() => shouldPerilRender('flood')}
        position={WATER_PERIL_INITIAL_POSITION}
        visible={false}
      >
        <WaterPeril />
      </AnimatePresence>

      <AnimationController
        animatableRef={bushfireRef}
        getIsPresent={() => shouldPerilRender('bushfire')}
      >
        <BushfirePeril ref={bushfireRef} animationProgress={0} />
      </AnimationController>

      <AnimationController
        animatableRef={cycloneRef}
        getIsPresent={() => shouldPerilRender('cyclone')}
      >
        <CyclonePeril ref={cycloneRef} animationProgress={0} />
      </AnimationController>
    </group>
  );

  return (
    <>
      <ambientLight
        position={[0, 10, 0]}
        intensity={AMBIENT_LIGHT_MAX_INTENSITY}
        ref={ambientLightRef}
      />

      {perils}
    </>
  );
};

export default PerilsScene;
